/**
 * Canonical JSON — RFC 8785 (JCS) with domain-restricted numbers.
 *
 * Split out of `hash.ts` (2026-07-29) so that code which needs ONLY the
 * canonical form — the console's staleness fingerprint, session 5 gate 9 —
 * can import it without pulling in `node:crypto`, which does not exist in a
 * browser. The split is structural: every byte this module produces is
 * identical to what `hash.ts` produced before it, and the shared test
 * vectors in `tests/vectors/canonical_json_vectors.json` continue to pin
 * both implementations. See `docs/CANONICAL_JSON_AND_HASHING 270726.md` for
 * the specification and `tools/canonical_json.py` for the Python twin.
 *
 * **Why RFC 8785 rather than the house format used up to 2026-07-27.** A
 * cross-language canonicalisation is a contract, and a published standard is a
 * better contract than a private one however carefully written. Adopting it
 * was free at the moment of adoption: JCS and the previous form produce
 * identical bytes for every value the instrument contains, because every key
 * is ASCII. The two differ only above the BMP, where JCS sorts by UTF-16 code
 * unit and the old form sorted by code point. It would have stopped being free
 * the moment a hash was persisted. (Architect review of Stage 1A, finding A2.)
 *
 * **The one deliberate deviation from JCS: numbers are domain-restricted.**
 * JCS serialises numbers with ECMAScript `Number::toString`, which this
 * runtime implements natively — but Python's `repr` does not agree with it
 * everywhere, and the Python side cannot verify a hash it cannot reproduce.
 * Both implementations therefore reject numbers outside the range where they
 * provably agree. Assessment data sits many orders of magnitude inside it.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue | undefined };

/** See `tools/canonical_json.py` for the derivation of these bounds. */
const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER;
const MIN_SAFE_FRACTION = 1e-4;
const MAX_SAFE_FRACTION = 1e16;

export class CanonicalisationError extends TypeError {
  constructor(message: string) {
    super(message);
    this.name = 'CanonicalisationError';
  }
}

function serializeNumber(n: number): string {
  if (!Number.isFinite(n)) {
    throw new CanonicalisationError(`${n} is not representable in JSON`);
  }
  // -0 would serialise as "0" here and "-0.0" in Python. No score is
  // meaningfully negative zero, so normalise rather than diverge.
  if (Object.is(n, -0) || n === 0) return '0';

  if (Number.isInteger(n)) {
    if (Math.abs(n) > MAX_SAFE_INTEGER) {
      throw new CanonicalisationError(
        `${n} exceeds the safe integer range (2^53-1); it would not survive ` +
          `a round trip through a double`,
      );
    }
    return String(n);
  }

  const magnitude = Math.abs(n);
  if (magnitude < MIN_SAFE_FRACTION || magnitude >= MAX_SAFE_FRACTION) {
    throw new CanonicalisationError(
      `${n} is outside the range where ECMAScript and Python number ` +
        `formatting provably agree [${MIN_SAFE_FRACTION}, ${MAX_SAFE_FRACTION}); ` +
        `see docs/CANONICAL_JSON_AND_HASHING 270726.md`,
    );
  }
  return String(n);
}

export function canonicalJson(value: JsonValue): string {
  if (value === null) return 'null';

  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      return serializeNumber(value);
    case 'string':
      // JSON.stringify applies exactly the escaping RFC 8785 requires: quote,
      // backslash and the C0 controls, with the short forms, nothing else.
      return JSON.stringify(value);
    default:
      break;
  }

  if (Array.isArray(value)) {
    // Array order is data, never sorted (RFC 8785 §3.2.3).
    return (
      '[' +
      value
        .map((v) => {
          if (v === undefined) {
            throw new CanonicalisationError('undefined in an array is ambiguous');
          }
          return canonicalJson(v);
        })
        .join(',') +
      ']'
    );
  }

  if (typeof value === 'object') {
    // RFC 8785 §3.2.3: sort by UTF-16 code unit, which is exactly what the
    // default string comparison does. The Python side has to ask for this
    // explicitly, because Python compares by code point.
    const entries = Object.keys(value)
      .filter((k) => value[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJson(value[k]!)}`);
    return '{' + entries.join(',') + '}';
  }

  throw new CanonicalisationError(`unsupported type ${typeof value}`);
}
